import {
  Fragment,
  ReactNode,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import BadgeModal from "./BadgeModal";
import { useGetPopUpQuery, useHasSeenPopUpMutation } from "api";
import RunningStrick from "components/BadgeSystem/RunningStrick";
import MissedStrick from "components/BadgeSystem/MissedStrick";
import StrickComplete from "components/BadgeSystem/StrickComplete";
import { sendEvent } from "ga4";
export interface IBadgeContext {
  handleOpenModal: (
    achievementType: string,
    sourceOfClick?: string,
    isMe?: boolean
  ) => void;
}

export const BadgeContext = createContext<IBadgeContext>({} as IBadgeContext);

export type BadgeHandle = {
  refetch: () => void;
};

type Props = {
  children: ReactNode;
};

export const BadgeProvider = forwardRef<BadgeHandle, Props>(
  ({ children }, ref) => {
    const [open, setOpen] = useState(false);
    const { data, refetch } = useGetPopUpQuery({
      fetchPolicy: "no-cache",
    });
    const [hasSeenPopUp] = useHasSeenPopUpMutation();
    const [{ achievementType, sourceOfClick }, setModalData] = useState<{
      achievementType?: string;
      sourceOfClick?: string;
      isMe?: boolean;
    }>({});

    const currentPopup = useMemo(() => {
      const currentPopup = data?.getPopUp?.data?.[0];
      if (currentPopup) {
        const { data, ...rest } = currentPopup;
        return { ...rest, data: JSON.parse(data as any) };
      }
      return undefined;
    }, [data?.getPopUp?.data]);

    useImperativeHandle(
      ref,
      () => {
        return {
          refetch,
        };
      },
      [refetch]
    );

    useEffect(() => {
      let timeout: NodeJS.Timeout;
      if (currentPopup?.popUpType && currentPopup?.popUpDelay) {
        timeout = setTimeout(() => {
          if (currentPopup?.popUpType) {
            sendEvent({
              event: "popup_Initialised",
              pop_up_type: currentPopup?.popUpType,
            });
          }
          setOpen(true);
        }, currentPopup?.popUpDelay);
      }
      return () => {
        clearTimeout(timeout);
      };
    }, [currentPopup, setOpen]);

    const onhandleClose = useCallback(async () => {
      try {
        if (achievementType) {
          sendEvent({
            event: "popup_cross_clicked",
            pop_up_type: achievementType,
          });
          setModalData({});
        } else {
          if (currentPopup?.popUpType) {
            sendEvent({
              event: "popup_cross_clicked",
              pop_up_type: currentPopup?.popUpType,
            });
            setModalData({});
          }
          await hasSeenPopUp({
            variables: { popUpType: currentPopup?.popUpType },
            update: (cache) => {
              cache.evict({
                id: cache.identify({
                  popUpType: currentPopup?.popUpType,
                  __typename: "popUp",
                }),
              });
            },
          });
        }
      } finally {
        setOpen(false);
      }
    }, [achievementType, currentPopup?.popUpType, hasSeenPopUp]);

    const handleOpenModal = useCallback(
      (achievementType: string, sourceOfClick?: string, isMe?: boolean) => {
        sendEvent({ event: "popup_Initialised", pop_up_type: achievementType });
        setModalData({ achievementType, sourceOfClick, isMe });
        setOpen(true);
      },
      [setModalData, setOpen]
    );

    const values = useMemo(() => {
      return { handleOpenModal };
    }, [handleOpenModal]);

    const handleUnderstood = useCallback(
      (cta_text: string) => async () => {
        try {
          if (achievementType) {
            sendEvent({
              event: "popup_cta_clicked",
              pop_up_type: achievementType,
              cta_text,
            });
            setModalData({});
          } else {
            if (currentPopup?.popUpType) {
              sendEvent({
                event: "popup_cta_clicked",
                pop_up_type: currentPopup?.popUpType,
                cta_text,
              });
              setModalData({});
            }
            await hasSeenPopUp({
              variables: { popUpType: currentPopup?.popUpType },
              update: (cache) => {
                cache.evict({
                  id: cache.identify({
                    popUpType: currentPopup?.popUpType,
                    __typename: "popUp",
                  }),
                });
              },
            });
          }
        } finally {
          setOpen(false);
        }
      },
      [achievementType, currentPopup?.popUpType, hasSeenPopUp]
    );

    const handlePopupClose = useCallback(() => {
      return hasSeenPopUp({
        variables: { popUpType: currentPopup?.popUpType },
        update: (cache) => {
          cache.evict({
            id: cache.identify({
              popUpType: currentPopup?.popUpType,
              __typename: "popUp",
            }),
          });
        },
      });
    }, [currentPopup?.popUpType, hasSeenPopUp]);

    const handleModalClose = useCallback(() => {
      setModalData({});
      setOpen(false);
    }, [setModalData, setOpen]);

    const memoizedAchievementComponent = useMemo(() => {
      if (achievementType === "PowerUserStreak") {
        return (
          <RunningStrick
            handleUnderstood={handleUnderstood("understood")}
            handleModalClose={onhandleClose}
          />
        );
      }

      if (achievementType === "PowerUser_WeeksStreakCompletion") {
        return (
          <StrickComplete
            achievementType={
              achievementType || (currentPopup?.popUpType as string)
            }
            sourceOfClick={sourceOfClick}
            handleModalClose={onhandleClose}
            handleCrossButtonClicked={onhandleClose}
          />
        );
      }

      return null;
    }, [
      achievementType,
      currentPopup?.popUpType,
      sourceOfClick,
      handleUnderstood,
      onhandleClose,
    ]);

    const memoizedPopupComponent = useMemo(() => {
      const popUpType = currentPopup?.popUpType as string;

      if (["PowerUser_WeekCompleted"].includes(popUpType)) {
        return (
          <RunningStrick
            handleUnderstood={handleUnderstood("understood")}
            handleModalClose={onhandleClose}
          />
        );
      }

      if (popUpType === "PowerUser_WeekMissed") {
        return (
          <MissedStrick
            handleUnderstood={handleUnderstood("understood")}
            handleModalClose={onhandleClose}
          />
        );
      }

      if (["PowerUser_StreakCompleted"].includes(popUpType)) {
        return (
          <StrickComplete
            achievementType={currentPopup?.data?.achievementType}
            sourceOfClick={currentPopup?.data?.sourceOfClick}
            handleCrossButtonClicked={onhandleClose}
            handleModalClose={handleModalClose}
            handlePopupClose={handlePopupClose}
          />
        );
      }

      return null;
    }, [
      currentPopup?.popUpType,
      currentPopup?.data?.achievementType,
      currentPopup?.data?.sourceOfClick,
      handleUnderstood,
      onhandleClose,
      handleModalClose,
      handlePopupClose,
    ]);

    console.log("BadgeProvider", values);
    return (
      <Fragment>
        <BadgeContext.Provider value={values}>{children}</BadgeContext.Provider>
        <BadgeModal open={open} scroll="body">
          {achievementType
            ? memoizedAchievementComponent
            : memoizedPopupComponent}
        </BadgeModal>
      </Fragment>
    );
  }
);

export const useBadgeContext = () => {
  const context = useContext(BadgeContext);
  if (!context) throw new Error("useBadgeContext must be used within BadgeProvider");
  return context;
};

export default BadgeContext;
